import { capitalize } from "lodash-es";

import { Option } from "src/base-components/Select";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { NodeIcon } from "src/design-system/NodeIcon";

export const BUNDLE_RULE_DESCRIPTION =
  "A bundle can contain one SDK Script per provider and version.";

type ProviderVersion = Pick<
  ProviderSdkConfigResponse,
  "provider" | "provider_sdk_version"
>;

export const formatProviderVersion = ({
  provider,
  provider_sdk_version,
}: ProviderVersion): string =>
  `${capitalize(provider)} ${provider_sdk_version}`;

const targetsSameProviderVersion = (
  a: ProviderVersion,
  b: ProviderVersion,
): boolean =>
  a.provider === b.provider &&
  a.provider_sdk_version === b.provider_sdk_version;

/**
 * Options for a multi-select of SDK Scripts. Scripts that would violate the
 * one-script-per-provider-version rule against the current selection are
 * disabled up front (with the reason as tooltip) instead of failing on submit.
 */
export const buildSdkScriptOptions = (
  configs: ProviderSdkConfigResponse[],
  selectedIds: string[],
): Option[] => {
  const selected = configs.filter((config) => selectedIds.includes(config.id));
  return configs.map((config) => {
    const conflict = selected.find(
      (candidate) =>
        candidate.id !== config.id &&
        targetsSameProviderVersion(candidate, config),
    );
    return {
      key: config.id,
      // Provider logo next to the name, both in the options list and in the
      // selected-state pills. Block-level `flex` (not `inline-flex`): inside
      // the pill's text span an inline box aligns to the text baseline, which
      // pushes the label off-centre from the pill's close icon.
      value: (
        <span className="flex min-w-0 items-center gap-1.5">
          {config.icon_path && (
            <NodeIcon imageSrc={config.icon_path} size="xs" />
          )}
          <span className="truncate">{config.name}</span>
        </span>
      ),
      description: formatProviderVersion(config),
      searchText: `${config.name} ${formatProviderVersion(config)}`,
      disabled: conflict
        ? `"${conflict.name}" already targets ${formatProviderVersion(config)}. ${BUNDLE_RULE_DESCRIPTION}`
        : false,
    };
  });
};

/**
 * Options for the "Add to existing Bundle" select. Bundles the script already
 * belongs to are omitted; bundles that already hold a script for the same
 * provider and version are disabled with the reason as tooltip.
 */
export const buildBundleOptions = (
  bundles: ProviderSdkBundleResponse[],
  script: ProviderSdkConfigResponse | undefined,
): Option[] =>
  bundles
    .filter(
      (bundle) => !bundle.configs.some((config) => config.id === script?.id),
    )
    .map((bundle) => {
      const conflict =
        script &&
        bundle.configs.find((config) =>
          targetsSameProviderVersion(config, script),
        );
      return {
        key: bundle.id,
        value: bundle.name,
        disabled: conflict
          ? `"${bundle.name}" already contains "${conflict.name}" for ${formatProviderVersion(conflict)}. ${BUNDLE_RULE_DESCRIPTION}`
          : false,
      };
    });
